import React, { useEffect } from 'react';
import { useField } from 'formik';
import classNames from '../../../utility/classNames';
import DescriptionTooltipModal from './DescriptionTooltipModal';
import Tooltip from '../../Tooltip';

function TextInput({
  label,
  description,
  tooltipHeading,
  tooltipFormula,
  required,
  type = 'text',
  unit,
  layout = 'default',
  placeholder,
  emphasize,
  preValue,
  disabled = false,
  ...props
}) {
  if (type === 'number')
    console.warn(
      'You are using the TextInput component with type "number". To take advantage of comma-separated values, use the NumberInput component instead.'
    );

  const [field, meta, helpers] = useField(props);
  const { value, touched, error } = meta;
  const { setValue, setTouched } = helpers;

  useEffect(() => {
    if (preValue && !value) {
      setValue(preValue);
    }
  }, []);

  const handleValueChange = (e) => {
    e.preventDefault();
    const { value } = e.target;
    setValue(value);
  };

  return (
    <div
      className={classNames(
        (layout === 'compact' || layout === 'tableNoLabel') && 'my-0 space-y-0',
        layout === 'tableNoLabel' && 'w-full'
      )}
    >
      <div
        className={classNames(
          'sm:grid sm:items-start sm:gap-2',
          (layout === 'compact' || layout === 'tableNoLabel') &&
            'sm:gap-4 pt-0 border-none',
          layout === 'tableNoLabel' ? 'sm:grid-cols-2' : 'sm:grid-cols-3-inputs'
        )}
      >
        {layout !== 'tableNoLabel' && (
          <label
            htmlFor={field.name}
            className={classNames(
              'h-full flex',
              description || (meta.touched && meta.error)
                ? 'items-start'
                : 'items-center',
              layout === 'stacked' && 'col-span-3'
            )}
          >
            <span className="text-md font-normal text-neutral-900">
              {label}
              {required && <span>*</span>}{' '}
              {description && layout === 'compact' && (
                <DescriptionTooltipModal description={description} />
              )}
              {description && layout !== 'compact' && (
                <Tooltip
                  text={description}
                  heading={tooltipHeading}
                  formula={tooltipFormula}
                  type="info"
                />
              )}
            </span>
          </label>
        )}

        <div
          className={classNames(
            'w-full',
            layout === 'stacked' ? 'col-span-3' : 'col-span-2',
            'relative rounded-md'
          )}
        >
          <input
            id={field.name}
            value={value ?? ''}
            onChange={handleValueChange}
            disabled={disabled}
            onBlur={() => setTouched(true)}
            type={type}
            {...props}
            placeholder={placeholder}
            className={classNames(
              'block w-full border focus:border-primary bg-neutral-input focus:outline-none focus:ring-transparent  text-md  h-8 rounded-md py-0 px-3 placeholder-neutral-500 placeholder-opacity-90',
              touched && error ? 'border-warning' : 'border-transparent',
              disabled ? 'text-neutral-500 text-opacity-50' : 'text-neutral-900'
            )}
          />

          {unit && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-neutral-500 text-md" id="price-currency">
                {unit}
              </span>
            </div>
          )}

          {meta.touched && meta.error ? (
            <div className="error text-base font-small text-warning ">
              {meta.error}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default TextInput;
